/**
 * Apps > "Connections on PCs: read-only tools" (components/McpReadOnlyToolsSection.tsx; owner, 2026-10-18: a read on
 * Gyro AI for Revit needs no acceptance, only a write does). GET/PUT /v1/admin/mcp-read-only-tools (gyro-svc
 * governanceRoutes.ts, mcpReadOnly.ts): per native MCP server, the tools whose calls run on the PC without a
 * confirmation card, each either always a read or a read only for named values of one argument. No React here, so the
 * rules are unit-tested (test/mcp-read-only.test.ts).
 */

export interface McpReadWhen {
  arg: string;
  in: string[];
}
export interface McpReadOnlyTool {
  tool: string;
  readWhen?: McpReadWhen;
}
export interface McpReadOnlyServer {
  server: string;
  displayName?: string;
  tools: McpReadOnlyTool[];
}
export interface McpReadOnlyLimits {
  servers: number;
  toolsPerServer: number;
  values: number;
  server: number;
  tool: number;
  arg: number;
  value: number;
  displayName: number;
}
/** GET/PUT /v1/admin/mcp-read-only-tools. */
export interface McpReadOnlyView {
  servers: McpReadOnlyServer[];
  /** True while nothing is stored: the seed is in effect. */
  isDefault: boolean;
  seed: McpReadOnlyServer[];
  limits: McpReadOnlyLimits;
}

/** What the section edits for one server: the key, the display name, and its tools as text (one per line). */
export interface McpReadOnlyRow {
  server: string;
  displayName: string;
  tools: string;
}

export const MCP_READ_ONLY_HINT =
  'One tool per line. A tool that is always a read: its name alone, such as revit_status. A tool that is a read only for ' +
  'some values of one argument: "run_revit_python when mode is read or preview". Anything not listed here, not marked ' +
  'read by the PC itself, and not a reviewed Bluebeam read is a write: the person confirms it with one click.';
export const MCP_READ_ONLY_SAVED_TEXT = 'Saved. It applies from the next question on every PC, and the change is in the audit trail.';
export const MCP_READ_ONLY_SEED_TEXT = 'The seed is in effect: nothing is stored, so a service update can still change these rules.';
export const MCP_READ_ONLY_STORED_TEXT = 'A stored list is in effect: it replaces the seed until "Use the seed" is pressed.';

const TOOL_LINE = /^(\S+)(?:\s+when\s+(\S+)\s+is\s+(.+))?$/u;

/** A tool as one line of the editor: `name`, or `name when arg is v1 or v2`. */
export function formatToolLine(tool: McpReadOnlyTool): string {
  return tool.readWhen ? `${tool.tool} when ${tool.readWhen.arg} is ${tool.readWhen.in.join(' or ')}` : tool.tool;
}

export function rowsOf(servers: McpReadOnlyServer[]): McpReadOnlyRow[] {
  return servers.map((entry) => ({ server: entry.server, displayName: entry.displayName ?? '', tools: entry.tools.map(formatToolLine).join('\n') }));
}

export type RowsParse = { ok: true; servers: McpReadOnlyServer[] } | { ok: false; row: number; text: string };

/**
 * The rows as the body the service takes, or the first problem (row index and a sentence), so a broken line is refused on
 * the screen and nothing is sent. The service validates again and names the field it refuses.
 */
export function parseRows(rows: McpReadOnlyRow[], limits: McpReadOnlyLimits): RowsParse {
  if (rows.length > limits.servers) return { ok: false, row: limits.servers, text: `At most ${limits.servers} servers.` };
  const servers: McpReadOnlyServer[] = [];
  const seen = new Set<string>();
  for (const [index, row] of rows.entries()) {
    const server = row.server.trim();
    if (!server) return { ok: false, row: index, text: 'Give the server its key (the name the PC lists under Connections).' };
    if (server.length > limits.server || /[\u0000-\u001f\u007f]/u.test(server)) return { ok: false, row: index, text: `A server key is at most ${limits.server} characters.` };
    const key = server.toLowerCase() === 'revit-ai' ? 'gyro-ai-for-revit' : server.toLowerCase();
    if (seen.has(key)) return { ok: false, row: index, text: `${server} is listed twice.` };
    seen.add(key);
    const displayName = row.displayName.trim();
    if (displayName && !/^[A-Za-z0-9][A-Za-z0-9 .&'()+-]{0,39}$/.test(displayName)) return { ok: false, row: index, text: `A display name is 1 to ${limits.displayName} letters, digits, spaces or . & ' ( ) + -.` };
    const tools: McpReadOnlyTool[] = [];
    const toolNames = new Set<string>();
    for (const rawLine of row.tools.split(/\r?\n/u)) {
      const line = rawLine.trim();
      if (!line) continue;
      const match = TOOL_LINE.exec(line);
      if (!match) return { ok: false, row: index, text: `"${line}" is not a tool line. Write the tool name, or "tool when argument is value or value".` };
      const [, tool, arg, values] = match;
      if (tool!.length > limits.tool) return { ok: false, row: index, text: `A tool name is at most ${limits.tool} characters.` };
      if (toolNames.has(tool!)) return { ok: false, row: index, text: `${tool} is listed twice for ${server}.` };
      toolNames.add(tool!);
      if (tools.length >= limits.toolsPerServer) return { ok: false, row: index, text: `At most ${limits.toolsPerServer} tools per server.` };
      if (arg === undefined) { tools.push({ tool: tool! }); continue; }
      if (arg.length > limits.arg) return { ok: false, row: index, text: `An argument name is at most ${limits.arg} characters.` };
      const list = [...new Set(values!.split(/\s+or\s+|,/u).map((value) => value.trim()).filter(Boolean))];
      if (list.length === 0 || list.length > limits.values) return { ok: false, row: index, text: `"${tool}" needs 1 to ${limits.values} values after "is".` };
      if (list.some((value) => value.length > limits.value)) return { ok: false, row: index, text: `A value is at most ${limits.value} characters.` };
      tools.push({ tool: tool!, readWhen: { arg, in: list } });
    }
    servers.push({ server, ...(displayName ? { displayName } : {}), tools });
  }
  return { ok: true, servers };
}

/** True when the rows say exactly what the service holds (nothing to save). */
export function rowsEqual(rows: McpReadOnlyRow[], servers: McpReadOnlyServer[], limits: McpReadOnlyLimits): boolean {
  const parsed = parseRows(rows, limits);
  return parsed.ok && JSON.stringify(parsed.servers) === JSON.stringify(servers);
}
